import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Printer, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { openPrintWindow } from '@/lib/letterhead';
import DatePickerField from '@/components/DatePickerField';

const VOCI = [
  ['lenzuola', 'Lenzuola'],
  ['federe', 'Federe'],
  ['coperte', 'Coperte'],
  ['copriletto', 'Copriletto'],
  ['coprimaterasso', 'Coprimaterasso'],
];

const VUOTO = { lenzuola: '', federe: '', coperte: '', copriletto: '', coprimaterasso: '', carrello: '', firma: '' };

export default function BiancheriaTab() {
  const [mese, setMese] = useState(new Date().toISOString().slice(0, 7));
  const [dati, setDati] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ ...VUOTO, data: new Date().toISOString().slice(0, 10) });
  const [saving, setSaving] = useState(false);

  const fetchDati = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/moduli/biancheria', { params: { mese } });
      setDati(r.data);
    } catch { toast.error('Errore caricamento registro biancheria'); }
    setLoading(false);
  }, [mese]);

  useEffect(() => { fetchDati(); }, [fetchDati]);

  const salva = async () => {
    setSaving(true);
    try {
      await api.put('/moduli/biancheria', {
        data: form.data,
        lenzuola: parseInt(form.lenzuola) || 0,
        federe: parseInt(form.federe) || 0,
        coperte: parseInt(form.coperte) || 0,
        copriletto: parseInt(form.copriletto) || 0,
        coprimaterasso: parseInt(form.coprimaterasso) || 0,
        carrello: form.carrello,
        firma: form.firma,
      });
      toast.success('Consegna biancheria registrata');
      setForm(f => ({ ...VUOTO, data: f.data }));
      fetchDati();
    } catch { toast.error('Errore salvataggio'); }
    setSaving(false);
  };

  const stampa = () => {
    if (!dati) return;
    let html = `<table><thead><tr><th>Data</th>${VOCI.map(([, l]) => `<th>${l}</th>`).join('')}<th>Carrello</th><th>Tot. pezzi</th><th>Firma</th></tr></thead><tbody>`;
    dati.righe.forEach(r => {
      html += `<tr><td>${new Date(r.data).toLocaleDateString('it-IT')}</td>${VOCI.map(([k]) => `<td style="text-align:center">${r[k] || 0}</td>`).join('')}<td>${r.carrello || ''}</td><td style="text-align:center"><strong>${r.tot_pezzi || 0}</strong></td><td>${r.firma || r.registrato_da || ''}</td></tr>`;
    });
    html += `<tr><td><strong>Totale mese</strong></td>${VOCI.map(([k]) => `<td style="text-align:center"><strong>${dati.totali[k]}</strong></td>`).join('')}<td></td><td style="text-align:center"><strong>${dati.totali.tot_pezzi}</strong></td><td></td></tr>`;
    html += '</tbody></table>';
    openPrintWindow('Registro Biancheria', html, { subtitle: `Mese ${mese}` });
  };

  if (loading && !dati) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div>;

  return (
    <div className="space-y-4" data-testid="biancheria-tab">
      <div className="flex flex-wrap items-center gap-2">
        <Input type="month" value={mese} onChange={e => setMese(e.target.value)} className="w-40 h-9" data-testid="biancheria-mese" />
        <Button size="sm" variant="outline" onClick={stampa} className="h-9" data-testid="biancheria-print">
          <Printer className="w-4 h-4 mr-1" />Stampa registro
        </Button>
      </div>

      <Card>
        <CardContent className="p-4 space-y-3">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Nuova consegna</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
            <div className="space-y-1">
              <p className="text-[10px] text-slate-500">Data arrivo</p>
              <DatePickerField className="h-9 text-xs" value={form.data} onChange={v => setForm(f => ({ ...f, data: v }))} testId="biancheria-data" />
            </div>
            {VOCI.map(([k, label]) => (
              <div className="space-y-1" key={k}>
                <p className="text-[10px] text-slate-500">{label}</p>
                <Input type="number" min="0" className="h-9 text-xs" value={form[k]}
                  onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} data-testid={`biancheria-${k}`} />
              </div>
            ))}
            <div className="space-y-1">
              <p className="text-[10px] text-slate-500">Carrello</p>
              <Input className="h-9 text-xs" value={form.carrello} onChange={e => setForm(f => ({ ...f, carrello: e.target.value }))} data-testid="biancheria-carrello" />
            </div>
            <div className="space-y-1">
              <p className="text-[10px] text-slate-500">Firma</p>
              <Input className="h-9 text-xs" value={form.firma} onChange={e => setForm(f => ({ ...f, firma: e.target.value }))} data-testid="biancheria-firma" />
            </div>
          </div>
          <Button size="sm" className="bg-brand hover:bg-brand-dark text-white" onClick={salva} disabled={saving} data-testid="biancheria-save">
            <Save className="w-4 h-4 mr-1" />{saving ? 'Salvataggio...' : 'Registra consegna'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0 overflow-auto max-h-[55vh]">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 sticky top-0">
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                <th className="px-3 py-2">Data</th>
                {VOCI.map(([, l]) => <th key={l} className="px-2 py-2 text-center">{l}</th>)}
                <th className="px-2 py-2">Carrello</th>
                <th className="px-2 py-2 text-center">Tot.</th>
                <th className="px-3 py-2">Firma</th>
              </tr>
            </thead>
            <tbody>
              {dati.righe.length === 0 && (
                <tr><td colSpan={9} className="px-3 py-6 text-center text-slate-400">Nessuna consegna registrata in questo mese.</td></tr>
              )}
              {dati.righe.map(r => (
                <tr key={r.data} className="border-t border-slate-100" data-testid={`biancheria-row-${r.data}`}>
                  <td className="px-3 py-1.5 font-medium text-slate-700">{new Date(r.data).toLocaleDateString('it-IT')}</td>
                  {VOCI.map(([k]) => <td key={k} className="px-2 py-1.5 text-center">{r[k] || 0}</td>)}
                  <td className="px-2 py-1.5 text-slate-500">{r.carrello || '-'}</td>
                  <td className="px-2 py-1.5 text-center font-semibold text-brand-dark">{r.tot_pezzi || 0}</td>
                  <td className="px-3 py-1.5 text-slate-500">{r.firma || r.registrato_da || '-'}</td>
                </tr>
              ))}
              {dati.righe.length > 0 && (
                <tr className="border-t-2 border-slate-200 bg-slate-50 font-semibold">
                  <td className="px-3 py-2">Totale mese</td>
                  {VOCI.map(([k]) => <td key={k} className="px-2 py-2 text-center">{dati.totali[k]}</td>)}
                  <td />
                  <td className="px-2 py-2 text-center text-brand-dark">{dati.totali.tot_pezzi}</td>
                  <td />
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
